import { useState, useEffect } from "react";
import { Col } from "react-bootstrap";
import DateSelector from "../../components/shared/fields/DateSelector";
import GridCard from "../../components/shared/gridCard/GridCard";
import Datatable from "../../components/shared/datatable/Datatable";
import Button from "../../components/shared/buttons/button";
import { PlusSvg } from "../../svgFiles/PlusSvg";
import MixingModal from "./MixingModal";
import useTranslator from "../../hooks/useTranslator";
import { convertToUserTimezone, formatDateAndTime, formatNumber, getCurrentDate, makePriorLotNumber } from "../../helpers/Utils";
import { PROJECT_DATE_FORMAT } from "../../helpers/constants";
import KgGreen from "../../images/kg_green.svg";
import PendingCase from "../../images/pendingCase.svg";
import KgRejected from "../../images/kg_rejected.svg";
import { useGetMixingLogsQuery, useGetMixingSummaryQuery, useGetMixingWasteMaterialQuery ,useGetMixingPendingQuery } from "./mixingApi";
import WasteMaterialModal from "./WasteMaterialModal";
import MixingPendingModal from "./MixingPendingModal";
import { getSettingVariable, SETTINGS } from "../../helpers/systemVariable";

const Mixing = () => {

  // console.log(SETTINGS.PALLET_GREEN_CASES);

  const { _t } = useTranslator();
  const currentDate = getCurrentDate("YYYY-MM-DD");
  const [date, setDate] = useState(currentDate);
  const [lotNumber, setLotNumber] = useState(makePriorLotNumber(currentDate));
  const [currentLotNumber] = useState(makePriorLotNumber(currentDate));
  const [page, setPage] = useState(1);

  useEffect(() => { setLotNumber(makePriorLotNumber(date)); }, [date]);

  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState("add");
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [addRecord, setAddRecord] = useState(false);
  const [showEditPendingModal, setShowEditPendingModal] = useState(false);
  const [showWasteModal, setShowWasteModal] = useState(false);
  const [filter, setFilter] = useState({});
  const [appliedFilters, setAppliedFilters] = useState({
    batch_code: "",
    date: "",
    status: "",
  });

  const handleFilterApply = (newFilters) => {
    setAppliedFilters(newFilters);
    setFilter(newFilters);
  };

  const mixingLogLimit = 10;

  const { data: logsData, isLoading, isFetching } = useGetMixingLogsQuery({
    page,
    per_page: mixingLogLimit,
    ...filter,
  }, { refetchOnMountOrArgChange: true });

  const { data: summaryData } = useGetMixingSummaryQuery({ date, batch_code: lotNumber });

  // Fetch waste material logs split by shift
  const { data: wasteData } = useGetMixingWasteMaterialQuery(
    { batch_code: lotNumber, waste_type: "mixing_waste" },
    { skip: !lotNumber }
  );

  // Fetch Mixing Pending Log 
  const {data : mixingPendingLog} =  useGetMixingPendingQuery(
    { batch_code: lotNumber },
    { skip: !lotNumber }
  );

  // Filter for mixing material belonging to mixing
  const mixingPendingKg = mixingPendingLog?.data
    ?.filter((r) => r.material_key === "mixing_material" && r.belongs_to === "mixing")
    ?.reduce((s, r) => s + (parseFloat(r.material_unit) || 0), 0) ?? 0;

  const morningWaste = wasteData?.data
    ?.filter((w) => w.shift === "morning")
    ?.reduce((s, w) => s + (parseFloat(w.weight_in_kg) || 0), 0) ?? 0;

  const eveningWaste = wasteData?.data
    ?.filter((w) => w.shift === "evening")
    ?.reduce((s, w) => s + (parseFloat(w.weight_in_kg) || 0), 0) ?? 0;

  const handleAdd  = () => { setAddRecord(true); setModalMode("add");  setSelectedRecord(null); setShowModal(true); };
  const handleEdit = (row) => { setAddRecord(true); setModalMode("edit"); setSelectedRecord(row);  setShowModal(true); };

  const listItems = [
    { label: _t("total_entered_weight",      "Total Entered Weight"),    value: formatNumber(summaryData?.data?.total_entered_weight ?? 0 ,0), icon: KgGreen },
    { label: _t("total_entered_from_brokerage", "Total KG From Brokerage"), value: formatNumber(summaryData?.data?.total_entered_from_brokerage ?? 0  ,0), icon: KgGreen },
    { label: _t("total_kg_from_processing",      "Total KG From Processing"),    value: formatNumber(summaryData?.data?.total_kg_from_rework ?? 0 , 0), icon: KgGreen },
    { 
      label: _t("mixing_pending_kg", "Mixing Pending KG"),
      value: formatNumber(summaryData?.data?.mixing_pending_kg?.material_unit ?? 0),
      icon: KgGreen,
      extra_info: true,
      info_message: summaryData?.data?.mixing_pending_kg?.material_description ?? "",
    },
    { label: _t("total_mixing_waste","Total Mixing Waste"),value: `${formatNumber(summaryData?.data?.total_mixing_waste ?? 0 , 0)} kg`, icon: KgGreen },
  ];

    
  const columns = [
    { label: _t("date",         "Date"),         accessor: "created_at",   cell: ({ value }) => value ? convertToUserTimezone(value) : "" },
    { label: _t("batch",        "Batch"),        accessor: "batch_code",   cell: ({ value }) => value ? `#${value}` : "" },
    { label: _t("kg_entered",   "KG Entered"),   accessor: "kg_entered"   },
    { label: _t("water_weight", "Water Weight"), accessor: "water_weight" },
    {
      label: _t("status", "Status"),
      accessor: "status",
      cell: ({ value }) => {
        const badgeClass =
          value === "ready_for_cart" ? "cmn_badge"
          : value === "mixing"       ? "cmn_badge inactive"
          : "cmn_badge rejected";
        const label =
          value === "mixing"         ? _t("status_mixing",         "Mixing")
          : value === "ready_for_cart" ? _t("status_ready_for_cart", "Ready for Cart")
          : value;
        return <span className={badgeClass}>{label}</span>;
      },
    },
    { label: _t("status_updated_at","Status Updated At"), accessor: "status_updated_at",   cell: ({ value }) => value ? formatDateAndTime(value, PROJECT_DATE_FORMAT) : "" },
    {
      label: _t("action", "Action"),
      accessor: "id",
      cell: ({ row }) => {
        const isView = row.status === "ready_for_cart";
        return (
          <Button
            size="small"
            className={isView ? "borderRedBtn" : ""}
            label={isView ? _t("view", "View") : _t("edit", "Edit")}
            onClick={() => handleEdit(row)}
          />
        );
      },
    },
  ];

  return (
    <div>
      {addRecord && (
        <MixingModal
          show={showModal}
          setShow={setAddRecord}
          record={selectedRecord}
          mode={modalMode}
          lotNumber={currentLotNumber}
        />
      )}
      {showWasteModal && (
        <WasteMaterialModal
          show={showWasteModal}
          setShow={setShowWasteModal}
          batchCode={lotNumber}
        />
      )}
      {showEditPendingModal && (
        <MixingPendingModal
          show={showEditPendingModal}
          setShow={setShowEditPendingModal}
          batchCode={lotNumber}
          record={mixingPendingLog?.data?.find(
            (r) => r.material_key === "mixing_material" && r.belongs_to === "mixing"
          ) ?? null}
        />
      )}

      {/* Overview tiles */}
      <div className="overview cmn_card mb-3">
        <div className="overview_header d-flex gap-2 align-items-center">
          <h6 className="mb-0 flex-grow-1">{_t("mixing_overview", "Mixing Overview")}</h6>
          <div className="d-flex gap-2 align-items-center">
            <h6 className="mb-0">{_t("batch_code", "Batch Code")}</h6>
            <Button label={`#${lotNumber ?? 0}`} size="small" className="borderRedBtn" />
            <DateSelector setDate={setDate} />
          </div>
        </div>
        <ul className="m-0 grid_card_wrapper">
          {listItems.map((list, index) => (
            <GridCard
              key={index}
              title={list.label}
              value={list.value}
              icon={list.icon}
              extra_info={list.extra_info}
              info_message={list.info_message}
              day={currentDate === date ? _t("common_today", "Today") : formatDateAndTime(date, PROJECT_DATE_FORMAT)}
            />
          ))}
        </ul>
      </div>

      {/* Two-column layout: table left, pending + waste right */}
      <div className="d-flex gap-2">
        {/* ── Left: Mixing logs table ── */}
        <Col lg={9}>
          <Datatable
            title={_t("mixing_logs", "Mixing Logs")}
            columns={columns}
            tableData={logsData?.data || ""}
            isLoading={isLoading || isFetching}
            buttons={date === currentDate ? [{ label: _t("add_mixing_log", "Add Mixing Log"), onClick: handleAdd, icon: PlusSvg }] : []}
            showPegination={true}
            page={page}
            onPageChange={setPage}
            showCount={true}
            pageLimit={mixingLogLimit}
            // showFilterButton={true}
            showFilter={handleFilterApply}
            filterModule="mixing_filter"
            filters={appliedFilters}
          />
        </Col>

        {/* ── Right: Mixing Pending + Waste Material Log ── */}
        <Col lg={3}>
          {/* Mixing Pending */}
          <div className="overview mb-3">
            <div className="d-flex gap-2 align-items-center pb-4">
              <h5 className="flex-grow-1 mb-0">{_t("mixing_pending", "Mixing Pending")}</h5>
            </div>
            <ul className="overview cmn_card m-0 grid_sorting_wrapper">
              <GridCard
                title={_t("mixing_pending", "Mixing Pending")}
                value={`${mixingPendingKg} ${_t("kg", "KG")}`}
                icon={PendingCase}
                showEdit={date === currentDate ? true : false}
                onEdit={() => setShowEditPendingModal(true)}
              />
            </ul>
          </div>

          {/* Waste Material Log */}
          <div className="overview cmn_card mb-3">
            <div className="overview_header d-flex gap-2 align-items-center p-3">
              <div className="flex-grow-1">
                <h6 className="mb-0">{_t("waste_material_log", "Waste Material Log")}</h6>
              </div>
              {date === currentDate && 
                <Button
                  size="small"
                  label={_t("update", "Update")}
                  icon={PlusSvg}
                  onClick={() => setShowWasteModal(true)}
                />
              }
            </div>

            <div className="p-2">
              <div className="rejected-card">
                <div className="d-flex align-items-center gap-3">
                  <div className="rejected-icon">
                    <img src={KgRejected} alt="Waste Material" style={{ width: "48px", height: "48px" }} />
                  </div>
                  <div className="flex-grow-1">
                    <h6 className="mb-2" style={{ fontSize: "14px", color: "#666" }}>
                      {_t("mixing_waste", "Mixing Waste")}
                    </h6>
                    <div className="d-flex gap-3">
                      <div className="rejected-shift-data">
                        <span style={{ fontSize: "12px", color: "#999" }}>{_t("morning", "Morning")}</span>
                        <h5 className="mb-0" style={{ fontSize: "20px", fontWeight: "600" }}>
                          {morningWaste}
                        </h5>
                      </div>
                      <div className="rejected-shift-data">
                        <span style={{ fontSize: "12px", color: "#999" }}>{_t("evening", "Evening")}</span>
                        <h5 className="mb-0" style={{ fontSize: "20px", fontWeight: "600", color: "#ee7225" }}>
                          {eveningWaste}
                        </h5>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Col>
      </div>
    </div>
  );
};

export default Mixing;
